import React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'sm';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  href?: undefined;
}

export interface ButtonLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  href: string;
}

type Props = ButtonProps | ButtonLinkProps;

function buildClass(variant: ButtonVariant, size: ButtonSize, fullWidth: boolean, className: string) {
  return [
    'ds-btn',
    'ds-btn-hover-scale',
    `ds-btn--${variant}`,
    size === 'sm' ? 'ds-btn--sm' : '',
    fullWidth ? 'ds-btn--full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

export const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
  (props, ref) => {
    const variant = props.variant ?? 'primary';
    const size = props.size ?? 'md';
    const fullWidth = props.fullWidth ?? false;
    const loading = props.loading ?? false;
    const className = props.className ?? '';
    const { children } = props;
    const disabled = 'disabled' in props ? props.disabled : undefined;

    const cls = buildClass(variant, size, fullWidth, className);
    const content = (
      <>
        {loading ? <span className="ds-btn__spinner" aria-hidden="true" /> : null}
        {children}
      </>
    );

    if ('href' in props && props.href) {
      const { href, variant: _v, size: _s, fullWidth: _f, loading: _l, ...anchorRest } = props;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={cls}
          aria-disabled={loading || disabled ? true : undefined}
          {...anchorRest}
        >
          {content}
        </a>
      );
    }

    const { variant: _v, size: _s, fullWidth: _f, loading: _l, ...buttonRest } = props as ButtonProps;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={buttonRest.type ?? 'button'}
        className={cls}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...buttonRest}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
